import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Toaster } from 'react-hot-toast'

import Analyzer from './pages/Analyzer'
import Generator from './pages/Generator'
import BreachCheck from './pages/BreachCheck'

const TOOLS = [
  { id: 'analyzer',  num: '01', label: 'Analyzer',     component: Analyzer },
  { id: 'generator', num: '02', label: 'Generator',    component: Generator },
  { id: 'breach',    num: '03', label: 'Breach check', component: BreachCheck },
]

function Wordmark() {
  return (
    <span className="font-display font-semibold text-[15px] tracking-tight text-ink select-none">
      securevault<span className="text-accent">.</span>
    </span>
  )
}

function App() {
  const [active, setActive] = useState('analyzer')
  const ActiveTool = TOOLS.find(t => t.id === active)?.component

  // 1/2/3 switch tools, unless the user is typing somewhere
  useEffect(() => {
    const onKeyDown = e => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (/^(input|textarea|select)$/i.test(e.target.tagName)) return
      const tool = TOOLS[Number(e.key) - 1]
      if (tool) setActive(tool.id)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="h-screen flex flex-col md:flex-row bg-surface-base text-ink">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-56 shrink-0 border-r border-surface-border px-4 py-5">
        <div className="px-2 mb-8">
          <Wordmark />
        </div>

        <nav className="flex flex-col gap-0.5" aria-label="Tools">
          {TOOLS.map(({ id, num, label }, i) => {
            const isActive = active === id
            return (
              <button
                key={id}
                onClick={() => setActive(id)}
                aria-current={isActive ? 'page' : undefined}
                className={`group flex items-center gap-3 px-2 py-2 rounded-lg text-sm text-left transition-colors duration-150 ${
                  isActive
                    ? 'bg-surface-muted text-ink'
                    : 'text-ink-muted hover:text-ink-secondary'
                }`}
              >
                <span className={`font-mono text-[11px] ${isActive ? 'text-accent' : 'text-ink-faint'}`}>
                  {num}
                </span>
                <span className="flex-1 font-medium">{label}</span>
                <kbd className={`kbd transition-opacity ${isActive ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`}>
                  {i + 1}
                </kbd>
              </button>
            )
          })}
        </nav>

        <div className="mt-auto px-2 space-y-3">
          <p className="text-xs text-ink-faint leading-relaxed">
            No accounts. Nothing you type is stored.
          </p>
          <div className="flex items-center gap-2 text-[11px] font-mono text-ink-faint">
            <span>v1.0</span>
            <span aria-hidden="true">·</span>
            <a
              href="https://github.com/dhruvvdave/securevault"
              target="_blank"
              rel="noreferrer"
              className="hover:text-ink-secondary transition-colors"
            >
              source
            </a>
          </div>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="md:hidden shrink-0 border-b border-surface-border px-4 pt-4 pb-3 space-y-3">
        <Wordmark />
        <nav className="flex gap-1.5" aria-label="Tools">
          {TOOLS.map(({ id, label }) => {
            const isActive = active === id
            return (
              <button
                key={id}
                onClick={() => setActive(id)}
                aria-current={isActive ? 'page' : undefined}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                  isActive
                    ? 'bg-surface-muted text-ink border border-surface-border'
                    : 'text-ink-muted border border-transparent'
                }`}
              >
                {label}
              </button>
            )
          })}
        </nav>
      </header>

      <main className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            {ActiveTool && <ActiveTool />}
          </motion.div>
        </AnimatePresence>
      </main>

      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 2500,
          style: {
            background: '#12151b',
            color: '#e6e9ee',
            border: '1px solid #232936',
            borderRadius: '0.625rem',
            fontSize: '0.8125rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          },
          success: { iconTheme: { primary: '#3ecf8e', secondary: '#0b0d10' } },
          error:   { iconTheme: { primary: '#ef6a6a', secondary: '#0b0d10' } },
        }}
      />
    </div>
  )
}

export default App
